<script setup lang="ts">

import { ButtonType } from 'vince-ui/types/common';
import { setUiScope } from 'vince-ui/composables/useUiScope';

const DIALOG_SCOPE_PREFIX = 'dialog:';

const emit = defineEmits<{
    cancelAction: [],
    mainAction: [],
    dismiss: []
}>();

const { t } = useI18n();

const visible = defineModel<boolean>('visible', { required: true });

const props = defineProps<{
    title?: string;
    hideFooter?: boolean;
    closeButtonLabel?: string;
    actionButtonLabel?: string;
    actionButtonDisabled?: boolean;
    actionButtonLoading?: boolean;
    scope?: string;
}>();

const slots = useSlots();

const instance = getCurrentInstance();
const scopeKey = `${DIALOG_SCOPE_PREFIX}${instance?.uid}`;

const resolveScopeValue = (): string | null => {
    const name = props.scope ?? (instance?.parent?.type as { __name?: string } | undefined)?.__name;
    if (!name) {
        return null;
    }
    return name.startsWith(DIALOG_SCOPE_PREFIX) ? name : `${DIALOG_SCOPE_PREFIX}${name}`;
};

const scopeValue = resolveScopeValue();

const applyScope = (value: string | null) => {
    if (scopeValue === null) {
        return;
    }
    setUiScope(scopeKey, value);
};

const hasSlot = (name: string): boolean => {
    return !!slots[name];
};

const mainActionPerformed = () => {
    emit('mainAction');
};

const dismiss = () => {
    visible.value = false;
    emit('dismiss');
};

const cancelAction = () => {
    emit('cancelAction');
};

watch(visible, (isVisible) => {
    applyScope(isVisible ? scopeValue : null);
}, { immediate: true });

onBeforeUnmount(() => {
    applyScope(null);
});
</script>

<template>
    <VLDialog
        :visible="visible"
        modal
        :pt="{headerActions: {
            style: {
                position: 'absolute', right: '4px', top: '4px'
            }
        }}"
        :close-on-escape="true"
        @update:visible="dismiss"
    >
        <template #closebutton>
            <VButton
                :type="ButtonType.LINK_UNDERLINE"
                icon="pi pi-times"
                @click="dismiss"
            />
        </template>

        <template #header>
            <div class="flex items-center justify-between mb-4 w-full mr-10">
                <div class="flex items-center gap-1">
                    <h2 v-if="!hasSlot('title')" class="font-light mr-2">
                        {{ title }}
                    </h2>
                    <h2 v-else class="font-light mr-2">
                        <slot name="title" />
                    </h2>
                    <div v-if="hasSlot('header-actions-left')">
                        <slot name="header-actions-left" />
                    </div>
                </div>

                <div v-if="hasSlot('header-actions-right')" class="flex justify-end flex-row">
                    <slot name="header-actions-right" />
                </div>
            </div>
        </template>

        <slot name="default" />

        <template v-if="!props.hideFooter" #footer>
            <div class="flex justify-between align-middle pt-4">
                <div v-if="hasSlot('footer-actions')" class="flex justify-start align-middle w-full">
                    <slot name="footer-actions" />
                </div>

                <div class="flex justify-end align-middle">
                    <VButton
                        :label="closeButtonLabel || t('common.actions.cancel')"
                        class="mr-2"
                        @click="cancelAction"
                    />

                    <div v-if="hasSlot('main-actions')" class="flex align-middle">
                        <slot name="main-actions" />
                    </div>

                    <VButton
                        v-if="actionButtonLabel"
                        :label="actionButtonLabel"
                        :loading="actionButtonLoading"
                        :disabled="actionButtonDisabled"
                        :type="ButtonType.SECONDARY"
                        @click="mainActionPerformed"
                    />
                </div>
            </div>
        </template>
    </VLDialog>
</template>
